import { useSearchParams } from 'next/navigation'
import { useEffect } from 'react'

import {
  defaultFilters,
  useMapStore,
  useSearchFilterStore,
  ZSearchFilter,
} from '@/entities/map'
import { useRouter } from '@/shared/i18n'

import {
  convertToFilterSearchParams,
  convertToMapSearhParams,
  convertToWebviewSearchParams,
} from '../lib/search-params'

type Props = {
  pathname: string
}

export const useMapSearchParams = ({ pathname }: Props) => {
  const router = useRouter()
  const searchParams = useSearchParams()

  const width = searchParams?.get('width') || null
  const height = searchParams?.get('height') || null
  const userId = searchParams?.get('userId') || null

  const filterActions = useSearchFilterStore(state => state.actions)

  const updateSearchParams = () => {
    const roomFilters = useSearchFilterStore.getState().filters
    const mapParams = useMapStore.getState().params
    const mapCenter = useMapStore.getState().center

    const filterSearchParams = convertToFilterSearchParams(roomFilters)
    const mapSearchParams = convertToMapSearhParams({
      zoomLevel: mapParams.zoomLevel,
      center: { lat: mapCenter.lat, lng: mapCenter.lng },
    })
    const webviewSearchParams = convertToWebviewSearchParams({
      width,
      height,
      userId,
    })

    router.replace(
      `${pathname}?${mapSearchParams}${filterSearchParams && `&${filterSearchParams}`}${webviewSearchParams && `&${webviewSearchParams}`}`,
    )
  }

  useEffect(() => {
    filterActions.updateFilter({
      ...ZSearchFilter.parse({
        ...defaultFilters,
        ...Object.fromEntries(searchParams?.entries() ?? []),
      }),
    })

    const unsubscribeFilter = useSearchFilterStore.subscribe(
      state => state.filters,
      updateSearchParams,
    )

    const unsubscribeMap = useMapStore.subscribe(
      state => state.center,
      updateSearchParams,
    )

    return () => {
      unsubscribeFilter()
      unsubscribeMap()
    }
  }, [])

  return { searchParams, width, height, userId, updateSearchParams }
}
